import React, { StrictMode } from 'react';
import { Provider } from 'react-redux';
import { EnhancedStore } from '@reduxjs/toolkit';
import { HelmetContext, Provider as HelmetProvider } from '@okapi/helmet-react';
import { RenderCallback } from '@okapi/core';
import { Provider as RemoteProvider, Remote } from '@okapi/remote';
import { GlobalRouter, Router } from '@okapi/router-react';
import { YMInitializer } from 'react-yandex-metrika';
import { setABVariant } from 'store/pageStore';
import { selectIsMobileView } from 'selectors';
import { Meta, ErrorBoundary, GoogleAnalyticsScript } from 'common/components';
import { App } from '../../app';
import { getGTMCode, getYaCode } from '../../selectors';
import { AB_TEST_NAMES } from '../../constants/ab-test-names';
import { servicesLandingAppStyles } from './styles';

export type EntryProps = {
	router: GlobalRouter;
	remoteContext: RenderCallback['remoteContext'];
} & (ClientSideEntryProps | ServerSideEntryProps);

type ClientSideEntryProps = {
	store: EnhancedStore;
	helmetContext?: never;
};

type ServerSideEntryProps = {
	store: EnhancedStore;
	helmetContext?: HelmetContext;
};

export function createEntry({ router, helmetContext, store, remoteContext }: EntryProps) {
	const { cookies } = store.getState();

	const yaCode = Number(getYaCode(store.getState()));
	const gtmCode = getGTMCode(store.getState());
	const isMobileView = selectIsMobileView(store.getState());

	AB_TEST_NAMES.forEach(test => {
		const experimentCookie = cookies.cookies?.[test];
		const variant = ['B', 'C'].includes(experimentCookie) ? experimentCookie : 'A'; // вариант А всегда используем для исходной версии
		store.dispatch(setABVariant({ [test]: variant }));
	});

	return () => (
		<StrictMode>
			<ErrorBoundary>
				<HelmetProvider context={helmetContext}>
					<Provider store={store}>
						{/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
						{/* @ts-ignore */}
						<RemoteProvider context={remoteContext}>
							<Router router={router}>
								<Meta />
								<div
									className={`services-landings-app ${servicesLandingAppStyles}`}
								>
									{!isMobileView && (
										<Remote
											widget="header-footer/header"
											id="services-landings-header"
											fallback={<div>Хедер не загрузился</div>}
										/>
									)}
									<main className="services-landings-app__main">
										<App />
									</main>
									{!isMobileView && (
										<Remote
											widget="header-footer/footer"
											id="services-landings-footer"
											fallback={<div>Футер не загрузился</div>}
										/>
									)}
								</div>
								<GoogleAnalyticsScript id={gtmCode} />
								{yaCode ? (
									<YMInitializer
										accounts={[yaCode]}
										options={{
											clickmap: true,
											trackLinks: true,
											accurateTrackBounce: true,
											webvisor: true,
										}}
										version="2"
									/>
								) : null}
							</Router>
						</RemoteProvider>
					</Provider>
				</HelmetProvider>
			</ErrorBoundary>
		</StrictMode>
	);
}
